"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import { useQuery } from "@tanstack/react-query";
import MagnifyingGlassIcon from "@heroicons/react/24/outline/MagnifyingGlassIcon";
import XMarkIcon from "@heroicons/react/24/outline/XMarkIcon";
import ArrowDownRightIcon from "@heroicons/react/24/solid/ArrowDownRightIcon";
import ArrowLongRightIcon from "@heroicons/react/24/solid/ArrowLongRightIcon";
import getAllCompanies from "@/lib/get-all-companies";
import { filterCompaniesByTitle } from "@/lib/company-search";
import { useTranslation } from "@/hooks/translationContext";
import ListItems from "@/components/views/modals/search/ListItems";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

interface CompanyOption {
  id: number;
  title: string;
  slug: string;
  description?: string;
}

export default function CompanySearch() {
  const router = useRouter();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");

  // Every discount company, loaded once on first open and sorted like the
  // company tab of the header search.
  const { data: companies = [], isLoading } = useQuery<CompanyOption[]>({
    queryKey: ["all-companies-search"],
    queryFn: async () => {
      const items = await getAllCompanies();

      return items
        .map((item: any) => ({
          id: item.id,
          title: item.title || item.descriptions?.[0]?.title || "",
          slug: item.slug || item.descriptions?.[0]?.slug || "",
          description: item.shortDescription,
        }))
        .filter((item: CompanyOption) => item.slug)
        .sort((a: CompanyOption, b: CompanyOption) =>
          a.title.localeCompare(b.title, "tr"),
        );
    },
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const results = useMemo(
    () =>
      filterCompaniesByTitle(companies, query).map((company) => ({
        ...company,
        onClick: () => router.push(`/${company.slug}-indirimleri`),
      })),
    [companies, query, router],
  );

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        inputRef.current?.blur();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const placeholder = t("company-search-placeholder", {
    defaultValue: "Şirketinizi Arayın !",
  });

  return (
    <div
      id="company-search"
      role="search"
      ref={containerRef}
      className="relative w-full md:w-[440px] z-20"
    >
      <div
        className={cn(
          "flex items-center gap-2 px-4 py-3 bg-white border rounded-full shadow-[0px_0px_6px_0px_rgba(88,88,88,0.25)] duration-150",
          isOpen ? "border-secondary" : "border-[#e8e8e8]",
        )}
      >
        <MagnifyingGlassIcon className="size-4 shrink-0 text-black" />

        <input
          id="company-search-input"
          ref={inputRef}
          type="text"
          value={query}
          autoComplete="off"
          aria-label={placeholder}
          placeholder={placeholder}
          onFocus={() => setIsOpen(true)}
          onClick={() => setIsOpen(true)}
          onChange={(event) => {
            setQuery(event.target.value);
            setIsOpen(true);
          }}
          className="w-full outline-none bg-transparent text-sm text-black font-medium tracking-[-0.14px] placeholder:text-black"
        />

        {query.length > 0 && (
          <button
            type="button"
            aria-label="Temizle"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
          >
            <XMarkIcon className="size-4 text-primary" />
          </button>
        )}
      </div>

      {isOpen && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-full mt-2 max-h-[60vh] overflow-y-auto rounded-2xl border border-gray-200 bg-white px-4 shadow-lg"
        >
          {isLoading ? (
            <CompanySearchLoading />
          ) : results.length > 0 ? (
            <ListItems
              searchType="company"
              data={results}
              selectedData={null}
              clickedAllData={() => {}}
              onClose={() => setIsOpen(false)}
            />
          ) : (
            <div className="py-4 text-sm text-gray-400 font-medium">
              {t("company-search-no-result", {
                defaultValue: "Aradığınız şirket bulunamadı.",
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CompanySearchLoading() {
  return (
    <div className="flex flex-col">
      {Array.from(Array(5).keys()).map((key) => (
        <div
          key={key}
          className="py-3 flex items-center justify-between border-b border-gray-200"
        >
          <div className="flex gap-2 items-center">
            <ArrowDownRightIcon className="size-4" />
            <div className="flex flex-col gap-1">
              <Skeleton className="w-[90px] h-5" />
              <Skeleton className="w-[170px] h-4" />
            </div>
          </div>
          <ArrowLongRightIcon className="size-4" />
        </div>
      ))}
    </div>
  );
}
